import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API_CONFIG } from "../config/api_config";
import { useAuth } from "../contexts/AuthContext";
import { signOut } from "../lib/services/authService";
interface DecisionNodeProps {
  selectedId?: string;
}

const DecisionNode: React.FC<DecisionNodeProps> = ({ selectedId }) => {
  const { isLoading, isAuthenticated } = useAuth();
  const [ready, setReady] = useState(false);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Get treeId from various sources
  const getTreeId = () => {
    if (selectedId) return selectedId;
    if (params.treeId) return params.treeId;

    const urlParams = new URLSearchParams(location.search);
    const queryTreeId = urlParams.get("treeId");
    if (queryTreeId) return queryTreeId;

    const stateTreeId = location.state?.treeId;
    if (stateTreeId) return stateTreeId;

    return "new";
  };

  const treeId = getTreeId();

  useEffect(() => {
    let mounted = true;
    let intervalId: number | undefined;

    const prepare = async () => {
      // Wait for auth initialization
      if (isLoading) return;

      if (!isAuthenticated) {
        // Redirect to login by changing the URL
        window.location.href = "/login";
        return;
      }

      try {
        const apiConfig = API_CONFIG();
        localStorage.setItem("API_URL", apiConfig.BASE_URL);
      } catch (e) {
        console.warn("Failed to set localStorage config:", e);
      }

      // Poll for legacy app readiness
      intervalId = window.setInterval(() => {
        if (mounted) {
          setReady(true);
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      }, 500);
    };

    prepare();

    return () => {
      mounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isLoading, isAuthenticated, treeId]);

  // Handle messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our domain
      if (event.origin !== window.location.origin) return;

      const { type, data } = event.data;

      switch (type) {
        case "LEGACY_NAVIGATE":
          if (data.path) {
            // Use React Router navigation instead of window.location
            navigate(data.path);
          }
          break;
        case "LEGACY_EXTERNAL_LINK":
          if (data.url) {
            // Navigate to external link in the current window
            globalThis.location.href = data.url;
          }
          break;
        case "LEGACY_LOGOUT":
          // Handle logout requests from legacy app
          signOut();
          break;
        case "LEGACY_READY":
          console.log("Legacy app is ready");
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #3498db",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  const src = `/legacy/index.html?treeId=${encodeURIComponent(treeId)}`;

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f5",
      }}
    >
      <iframe
        title="Decision Node Legacy Editor"
        src={src}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
          backgroundColor: "#f5f5f5",
        }}
        allow="fullscreen"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
      />
    </div>
  );
};

export default DecisionNode;
